/**
 * Pipefy Core - Frontend JavaScript
 */

import { initPipefyDataLayerNavigation } from './tracking/data-layer-navigation.js';
import { initViewportAnimations } from './animations';
import { initCountUp } from './animations/count-up';
import { lazyObserve } from './utils/lazyObserve';
import { initBookADemoChatRedirect } from './components/book-a-demo-chat-redirect';
import { initUseNowCtaTracking } from './use-now-tracking.js';
import './components/mega-menu-card-tilt';

// Global Pipefy Core object
window.PipefyCore = {
  version: '1.0.0',
  init() {
    this.stickyHeader();
    this.mobileSubmenuToggle();
    this.languageSwitcherDelegation();
    initUseNowCtaTracking();
    initBookADemoChatRedirect();
    this.initBookADemoNewCustomElements();

    /**
     * Custom elements to initialize
     * Need to specify the folder where the custom element is located
     * Example:
     * {
     *   'pipefy-metrics': 'blocks',
     *   'pipefy-accordion': 'blocks',
     *   'pipefy-vertical-tabs': 'blocks',
     *   'pipefy-testimonial': 'blocks',
     *   'pipefy-horizontal-tabs': 'blocks',
     * }
     */
    this.initCustomElement({
      'pipefy-metrics': 'blocks',
      'pipefy-accordion': 'blocks',
      'pipefy-vertical-tabs': 'blocks',
      'pipefy-testimonial': 'blocks',
      'pipefy-horizontal-tabs': 'blocks',
      'pipefy-bubble-tabs': 'blocks',
      'pipefy-video-block': 'blocks',
      'pipefy-narrative-video': 'blocks',
      'pipefy-process-evolution': 'blocks',
      'pipefy-choose-your-plan': 'blocks',
      'pipefy-faq': 'blocks',
      'pipefy-segment-overview-card': 'blocks',
      'pipefy-banner-hero': 'blocks',
      'pipefy-success-hero': 'blocks',
      'pipefy-workflow-overview': 'blocks',
      'pipefy-process-flow': 'blocks',
      'pipefy-use-cases': 'blocks',
      'pipefy-highlight-cards': 'blocks',
      'pipefy-vertical-buttons-tabs': 'blocks',
      'pipefy-success-slider': 'blocks',
      'pipefy-related-blog-slider': 'blocks',
      'pipefy-slider-cards': 'blocks',
      'pipefy-culture-values': 'blocks',
      'pipefy-accordion-grid': 'blocks',
      'pipefy-card-slider': 'blocks',
      'pipefy-card-grid': 'blocks',
      'pipefy-featured-collection': 'blocks',
      'pipefy-roles-hero': 'blocks',
      'pipefy-ads-hero': 'blocks',
      'pipefy-partners-form': 'blocks',
      'pipefy-logo-row-cards': 'blocks',
      'pipefy-brand-partners': 'blocks',
      'pipefy-agent-chat': 'blocks',
      'pipefy-agents-section': 'blocks',
    });
    this.initEnableLightboxClass();
    this.initAnimate();
  },
  initEnableLightboxClass() {
    lazyObserve('.enable-lightbox', el => {
      import('./components/enable-lightbox.js').then(({ initEnableLightboxClass }) =>
        initEnableLightboxClass(el)
      );
    });
  },
  initBookADemoNewCustomElements() {
    const isBookADemoNewPage = document.querySelector('[data-book-a-demo-new-page]') !== null;

    if (!isBookADemoNewPage) {
      return;
    }

    // Eager import prevents race conditions when testimonial nodes are hydrated/morphed by Livewire.
    import('./blocks/testimonial.js').catch(() => {});
  },
  stickyHeader() {
    const header = document.querySelector('.pfy-core-header');
    if (!header) return;
    if (header.classList.contains('pfy-template-header-static')) {
      return;
    }

    const onScroll = () => {
      if (window.scrollY > 45) {
        header.classList.add('sticky');
      } else {
        header.classList.remove('sticky');
      }
    };

    // Apply initial state on load and whenever user scrolls
    window.addEventListener('scroll', onScroll);
    window.addEventListener('load', onScroll);
    // Ensure state is set immediately after DOM is ready
    onScroll();
  },
  mobileSubmenuToggle() {
    const menuRoot = document.querySelector('.pfy-core-mega-menu');
    if (!menuRoot) return;

    const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;
    const closeAllSubmenus = () => {
      menuRoot.querySelectorAll('li.is-open').forEach(li => {
        li.classList.remove('is-open');
        const toggle = li.querySelector('a.js-submenu-toggle');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    };

    const toggles = menuRoot.querySelectorAll('a.js-submenu-toggle');
    toggles.forEach(toggle => {
      toggle.addEventListener('click', event => {
        if (isDesktop()) return;

        event.preventDefault();
        event.stopPropagation();

        const parentItem = toggle.closest('li');
        if (!parentItem) return;

        const currentlyOpen = parentItem.classList.contains('is-open');

        // Close any other open siblings at same level
        const siblings = parentItem.parentElement?.querySelectorAll(':scope > li.is-open') || [];
        siblings.forEach(sibling => {
          if (sibling !== parentItem) {
            sibling.classList.remove('is-open');
            const siblingToggle = sibling.querySelector('a.js-submenu-toggle');
            if (siblingToggle) siblingToggle.setAttribute('aria-expanded', 'false');
          }
        });

        // Toggle current
        if (currentlyOpen) {
          parentItem.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
        } else {
          parentItem.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
          // On small mobile only, scroll the item into view to avoid jumpiness on tablets
          parentItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });
    });

    // Mobile a11y: ESC closes any open submenu(s)
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      if (isDesktop()) return;
      if (!menuRoot.querySelector('li.is-open')) return;

      closeAllSubmenus();
    });

    // On desktop, remove lingering focus from other toggles when hovering a new item
    menuRoot.addEventListener('mouseover', event => {
      if (!isDesktop()) return;
      const hoveredItem = event.target.closest('li');
      if (!hoveredItem || !menuRoot.contains(hoveredItem)) return;
      toggles.forEach(toggle => {
        if (!hoveredItem.contains(toggle) && document.activeElement === toggle) {
          toggle.blur();
        }
      });
    });

    // Ensure no lingering "is-open" state on desktop when pointer leaves or viewport switches
    menuRoot.addEventListener('mouseleave', () => {
      if (isDesktop()) closeAllSubmenus();
    });

    const desktopMedia = window.matchMedia('(min-width: 1024px)');
    desktopMedia.addEventListener('change', e => {
      if (e.matches) closeAllSubmenus();
    });
  },
  languageSwitcherDelegation() {
    if (this._langSwitcherBound) return;
    this._langSwitcherBound = true;

    const SWITCHER = '.pfy-language-switcher';
    const BTN_SEL = `${SWITCHER} > button`;
    const ATTR = 'aria-expanded';
    const $all = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
    const isOpen = btn => btn.getAttribute(ATTR) === 'true';
    const set = (btn, open) => btn.setAttribute(ATTR, open ? 'true' : 'false');
    const closeAll = () => $all(`${BTN_SEL}[${ATTR}="true"]`).forEach(b => set(b, false));

    // init
    $all(BTN_SEL).forEach(b => {
      if (!b.hasAttribute(ATTR)) set(b, false);
    });

    // click: toggle or close if outside
    document.addEventListener('click', e => {
      const btn = e.target.closest(BTN_SEL);
      if (btn) {
        e.preventDefault();
        set(btn, !isOpen(btn));
        return;
      }
      if (!e.target.closest(SWITCHER)) closeAll();
    });

    // esc: close all
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeAll();
    });
  },
  async initCustomElement($elements) {
    Object.entries($elements).forEach(([element, folder]) => {
      lazyObserve(element, async el => {
        try {
          await import(`./${folder}/${element.replace('pipefy-', '')}.js`);
        } catch (error) {
          if (element === 'pipefy-agent-chat') {
            el.removeAttribute('data-animation-ready');
          }

          throw error;
        }
      });
    });
  },
  initAnimate(root = document) {
    initViewportAnimations('[data-animate]', { defaultOffset: '100px', root });
    initCountUp('[data-count-up]', { root });
  },
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  initPipefyDataLayerNavigation();
  window.PipefyCore.init();
});
